import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Pagination,
  SearchWithFilter,
  SectionCard,
  Tabs,
  type FilterSelection,
} from '@enamelai/design-system'
import { Icon as ArrowRightIcon } from '@enamelai/design-system/icons/Arrow_2_right'
import { Icon as CallIcon } from '@enamelai/design-system/icons/Call'
import { Icon as FaxIcon } from '@enamelai/design-system/icons/Fax'
import { Icon as InboxIcon } from '@enamelai/design-system/icons/Inbox'
import { Icon as MailIcon } from '@enamelai/design-system/icons/Mail_fill'
import { Icon as MessageIcon } from '@enamelai/design-system/icons/Message'
import { DEMO_COMMUNICATIONS_HISTORY } from '@assets/mockdata/PatientTray/mockCommunicationsHistory'
import ProfileCommunicationHistoryRow from '@pages/PatientProfile/shared/ProfileCommunicationHistoryRow.component'
import ProfileEmptyState from '@pages/PatientProfile/shared/ProfileEmptyState.component'
import {
  buildPatientProfileMessagePreviousPage,
  navigateToMessageFromCommunicationHistory,
} from '@utils/patientProfileCommunicationNavigation.util'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { fetchCommunicationHistory } from '@store/actions'
import {
  formatItemTitle,
  getRecentHistoryItems,
} from '@utils/communicationHistory.util'
import './PatientProfileCommunicationsHistory.css'
import {
  CommunicationChannelFilter,
  CommunicationDirection,
  ICommunicationHistory,
} from '@dataTypes/communicationHistory.type'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

const CHANNEL_TAB_LABELS: Record<CommunicationChannelFilter, string> = {
  [CommunicationChannelFilter.All]: 'All',
  [CommunicationChannelFilter.Calls]: 'Calls',
  [CommunicationChannelFilter.SMS]: 'Chats',
  [CommunicationChannelFilter.Email]: 'Emails',
  [CommunicationChannelFilter.Fax]: 'Fax',
}

const CHANNEL_TAB_ITEMS = Object.values(CommunicationChannelFilter).map(
  (channel) => ({ id: channel, label: CHANNEL_TAB_LABELS[channel] }),
)

type ChannelEmptyState = {
  title: string
  description: string
  icon: React.FC<React.SVGProps<SVGSVGElement>>
}

const CHANNEL_EMPTY_STATE: Record<
  CommunicationChannelFilter,
  ChannelEmptyState
> = {
  [CommunicationChannelFilter.All]: {
    title: 'No communications yet',
    description:
      'Calls, SMS, emails, and faxes with this patient will appear here.',
    icon: InboxIcon,
  },
  [CommunicationChannelFilter.Calls]: {
    title: 'No calls yet',
    description: 'Call history with this patient will appear here.',
    icon: CallIcon,
  },
  [CommunicationChannelFilter.SMS]: {
    title: 'No SMS yet',
    description: 'Text messages with this patient will appear here.',
    icon: MessageIcon,
  },
  [CommunicationChannelFilter.Email]: {
    title: 'No emails yet',
    description: 'Email history with this patient will appear here.',
    icon: MailIcon,
  },
  [CommunicationChannelFilter.Fax]: {
    title: 'No faxes yet',
    description: 'Fax history with this patient will appear here.',
    icon: FaxIcon,
  },
}

const STATUS_FILTER_OPTIONS = [
  'Received',
  'Sent',
  'Read',
  'Delivered',
  'Connected',
  'Missed',
]

const DIRECTION_FILTER_OPTIONS = ['Inbound', 'Outbound']

const matchesStatus = (
  item: ICommunicationHistory,
  selectedStatuses: string[],
): boolean => {
  if (selectedStatuses.length === 0) return true
  return selectedStatuses.includes(item.status)
}

const matchesDirection = (
  item: ICommunicationHistory,
  selectedDirections: string[],
): boolean => {
  if (selectedDirections.length === 0) return true

  const wantsInbound = selectedDirections.includes('Inbound')
  const wantsOutbound = selectedDirections.includes('Outbound')

  if (wantsInbound && wantsOutbound) return true
  if (wantsInbound) return item.direction === CommunicationDirection.Inbound
  if (wantsOutbound) return item.direction === CommunicationDirection.Outbound

  return true
}

const getEmptyPlaceholderState = (
  channelFilter: CommunicationChannelFilter,
  allItemsCount: number,
  filteredItemsCount: number,
  searchQuery: string,
  listFilters: FilterSelection,
): ChannelEmptyState => {
  const base = CHANNEL_EMPTY_STATE[channelFilter]
  const query = searchQuery.trim()
  const hasActiveSearch = query.length > 0
  const hasActiveStatusFilter = (listFilters.Status ?? []).length > 0
  const hasActiveDirectionFilter = (listFilters.Direction ?? []).length > 0
  const hasActiveClientFilters =
    hasActiveSearch || hasActiveStatusFilter || hasActiveDirectionFilter

  if (allItemsCount > 0 && filteredItemsCount === 0 && hasActiveClientFilters) {
    let description =
      'No communications match the selected filters. Try adjusting status or direction.'
    if (
      hasActiveSearch &&
      (hasActiveStatusFilter || hasActiveDirectionFilter)
    ) {
      description = `No results found. Try adjusting your search or filters.`
    } else if (hasActiveSearch) {
      description = `No results for "${query}". Try a different search or clear your filters.`
    }
    return {
      icon: base.icon,
      title: 'No matching communications',
      description,
    }
  }
  return base
}

const SKELETON_ROW_COUNT = 6

function CommunicationsHistorySkeleton(): React.ReactElement {
  return (
    <div
      className="patient-profile-communications-skeleton"
      role="status"
      aria-label="Loading communications history"
    >
      {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
        <div
          key={index}
          className="patient-profile-communications-skeleton-row"
          aria-hidden
        >
          <span className="patient-profile-communications-skeleton-bar patient-profile-communications-skeleton-bar--icon" />
          <div className="patient-profile-communications-skeleton-content">
            <span className="patient-profile-communications-skeleton-bar patient-profile-communications-skeleton-bar--title" />
            <span className="patient-profile-communications-skeleton-bar patient-profile-communications-skeleton-bar--meta" />
            <span className="patient-profile-communications-skeleton-bar patient-profile-communications-skeleton-bar--text" />
          </div>
          <div className="patient-profile-communications-skeleton-aside">
            <span className="patient-profile-communications-skeleton-bar patient-profile-communications-skeleton-bar--time" />
            <span className="patient-profile-communications-skeleton-bar patient-profile-communications-skeleton-bar--badge" />
          </div>
        </div>
      ))}
    </div>
  )
}

type PatientProfileCommunicationsHistoryProps = {
  patientId?: string
  useDemoData?: boolean
  forceEmptyStates?: boolean
  onCommunicationClick?: (item: ICommunicationHistory) => void
}

function PatientProfileCommunicationsHistory({
  patientId,
  useDemoData = true,
  forceEmptyStates = false,
  onCommunicationClick,
}: PatientProfileCommunicationsHistoryProps): React.ReactElement {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { communicationLog, loading } = useAppSelector(
    (state) => state.patientProfile,
  )
  const [channelFilter, setChannelFilter] =
    useState<CommunicationChannelFilter>(CommunicationChannelFilter.All)
  const [searchQuery, setSearchQuery] = useState('')
  const [listFilters, setListFilters] = useState<FilterSelection>({
    Status: [],
    Direction: [],
  })

  // Tracks whether the first request for this patient has settled, so the
  // list shows the skeleton (not the empty state) before the fetch starts.
  const [fetchedForPatientId, setFetchedForPatientId] = useState<string | null>(
    null,
  )

  useEffect(() => {
    if (useDemoData || !patientId) return
    let cancelled = false
    dispatch(fetchCommunicationHistory(patientId)).finally(() => {
      if (!cancelled) setFetchedForPatientId(patientId)
    })
    return () => {
      cancelled = true
    }
  }, [dispatch, patientId, useDemoData])

  const isLoadingHistory =
    !forceEmptyStates &&
    !useDemoData &&
    Boolean(patientId) &&
    (loading.communication || fetchedForPatientId !== patientId)

  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0])

  const allItems = useMemo<ICommunicationHistory[]>(() => {
    const source = useDemoData ? DEMO_COMMUNICATIONS_HISTORY : communicationLog
    return getRecentHistoryItems(source, channelFilter, source.length)
  }, [useDemoData, communicationLog, channelFilter])

  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    const selectedStatuses = listFilters.Status ?? []
    const selectedDirections = listFilters.Direction ?? []

    return allItems.filter((item) => {
      if (!matchesStatus(item, selectedStatuses)) return false
      if (!matchesDirection(item, selectedDirections)) return false

      if (!query) return true

      const haystack = [
        formatItemTitle(item),
        item.patientName,
        item.text,
        item.timestamp,
        item.status,
      ]
        .join(' ')
        .toLowerCase()

      return haystack.includes(query)
    })
  }, [allItems, searchQuery, listFilters])

  useEffect(() => {
    setCurrentPage(1)
  }, [channelFilter, searchQuery, listFilters])

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize))
  const safePage = Math.min(currentPage, totalPages)
  const pagedItems = useMemo(
    () => filteredItems.slice((safePage - 1) * pageSize, safePage * pageSize),
    [filteredItems, safePage, pageSize],
  )

  const handlePageSizeChange = (nextPageSize: number): void => {
    setPageSize(nextPageSize)
    setCurrentPage(1)
  }

  const emptyPlaceholderState = useMemo(
    () =>
      getEmptyPlaceholderState(
        channelFilter,
        allItems.length,
        filteredItems.length,
        searchQuery,
        listFilters,
      ),

    [
      channelFilter,
      allItems.length,
      filteredItems.length,
      searchQuery,
      listFilters,
    ],
  )

  const handleCommunicationClick = (item: ICommunicationHistory): void => {
    if (onCommunicationClick) {
      onCommunicationClick(item)
      return
    }
    if (!patientId) return
    navigateToMessageFromCommunicationHistory(
      navigate,
      item,
      buildPatientProfileMessagePreviousPage(patientId, 'communications'),
    )
  }

  const handleOpenMessageCenter = (): void => {
    if (!patientId) {
      navigate('/message')
      return
    }
    navigate('/message', {
      state: {
        previousPage: buildPatientProfileMessagePreviousPage(
          patientId,
          'communications',
        ),
      },
    })
  }

  return (
    <div className="patient-profile-communications">
      <SectionCard className="patient-profile-section patient-profile-communications-card">
        <div className="patient-profile-communications-toolbar">
          <Tabs
            items={CHANNEL_TAB_ITEMS}
            value={channelFilter}
            onChange={(value) =>
              setChannelFilter(value as CommunicationChannelFilter)
            }
            className="patient-profile-communications-filters"
          />
          <div className="patient-profile-communications-toolbar-end">
            <SearchWithFilter
              placeholder="Search"
              value={searchQuery}
              onChange={setSearchQuery}
              className="patient-profile-communications-search"
              filters={{
                Status: STATUS_FILTER_OPTIONS,
                Direction: DIRECTION_FILTER_OPTIONS,
              }}
              filterLabels={{
                Status: 'Status',
                Direction: 'Direction',
              }}
              selectedFilters={listFilters}
              onFilterChange={setListFilters}
            />
            <Button
              variant="primary"
              size="md"
              rightIcon={
                <ArrowRightIcon aria-hidden="true" width={16} height={16} />
              }
              onClick={handleOpenMessageCenter}
            >
              View in communications center
            </Button>
          </div>
        </div>

        <div
          className="patient-profile-communications-list"
          aria-busy={isLoadingHistory}
        >
          {isLoadingHistory ? (
            <CommunicationsHistorySkeleton />
          ) : forceEmptyStates || filteredItems.length === 0 ? (
            <ProfileEmptyState
              icon={emptyPlaceholderState.icon}
              title={emptyPlaceholderState.title}
              description={emptyPlaceholderState.description}
              className="patient-profile-communications-empty-state"
            />
          ) : (
            pagedItems.map((item) => (
              <ProfileCommunicationHistoryRow
                key={item.id}
                item={item}
                onClick={handleCommunicationClick}
              />
            ))
          )}
        </div>
        {!forceEmptyStates && filteredItems.length > 0 ? (
          <Pagination
            className="patient-profile-communications-pagination"
            currentPage={safePage}
            totalPages={totalPages}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageChange={setCurrentPage}
            onPageSizeChange={handlePageSizeChange}
          />
        ) : null}
      </SectionCard>
    </div>
  )
}

export default PatientProfileCommunicationsHistory
